import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authorizedFetch } from "../auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";
import type {
  ClientDetail,
  ClientListItem,
  ClientSecretResponse,
  CreateClientRequest,
  LocalClientResponse,
  UserSearchResult,
} from "./types";

const API_BASE = import.meta.env.VITE_KEYCLOAK_API_URL ?? "/keycloak-api";

export function useClients() {
  return useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_BASE}/clients`);
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<ClientListItem[]>;
    },
    staleTime: 1000 * 60,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateClientRequest) => {
      const res = await authorizedFetch(`${API_BASE}/clients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<ClientDetail>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

// Shared by `useClient` and by callers that fetch many clients with
// `useQueries`, so both hit the same cache entry.
export function clientQuery(id: string) {
  return {
    queryKey: ["clients", id],
    enabled: !!id,
    queryFn: async () => {
      const res = await authorizedFetch(`${API_BASE}/clients/${id}`);
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<ClientDetail>;
    },
  };
}

export function useClient(id: string) {
  return useQuery(clientQuery(id));
}

// Attach a client to a Platform (platformId set) or detach to standalone
// (platformId null). Soft grouping only — never deletes the client (ADR-0007).
export async function updateClientMembership(
  clientId: string,
  platformId: string | null,
) {
  const res = await authorizedFetch(`${API_BASE}/clients/${clientId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ platformId }),
  });
  await throwIfNotOk(res, "kc");
}

export function useUpdateClientMembership(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (platformId: string | null) =>
      updateClientMembership(clientId, platformId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", clientId] });
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
    },
  });
}

export function useUpdateRedirectUris() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      redirectUris,
    }: {
      id: string;
      redirectUris: string[];
    }) => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${id}/redirect-uris`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ redirectUris }),
        },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", variables.id] });
    },
  });
}

export function useUpdateClientDetails(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Full replacement on the PUT: the stack must be sent on every call, or an
    // edit of the name or description clears it (ADR-0060 decision 6).
    mutationFn: async ({
      name,
      description,
      stack,
    }: {
      name: string;
      description: string;
      stack: string | null;
    }) => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/details`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, description, stack }),
        },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", clientId] });
    },
  });
}

// The wired-up claim (ADR-0060 decision 5): the maintainer's self-reported
// assertion that a real application authenticates through this client. True
// marks, false unmarks; the server stamps the time, so the request carries
// only the intent.
export function useMarkClientWiredUp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, wiredUp }: { id: string; wiredUp: boolean }) => {
      const res = await authorizedFetch(`${API_BASE}/clients/${id}/wired-up`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wiredUp }),
      });
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<{ wiredUpAt: string | null }>;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", variables.id] });
    },
  });
}

export function useUpdateClientSpiffe(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      spiffeNamespace,
      spiffeServiceAccount,
      spiffeCluster,
    }: {
      spiffeNamespace?: string;
      spiffeServiceAccount?: string;
      spiffeCluster?: string;
    }) => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/spiffe`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            spiffeNamespace: spiffeNamespace || null,
            spiffeServiceAccount: spiffeServiceAccount || null,
            spiffeCluster: spiffeCluster || null,
          }),
        },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", clientId] });
    },
  });
}

export function useUpdateClientTags(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      purposeTag,
      tags,
    }: {
      purposeTag: string;
      tags: string[];
    }) => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/tags`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ purposeTag, tags }),
        },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", clientId] });
    },
  });
}

// Change the type between api and worker (ADR-0044). The server refuses a
// downgrade with kc.audience_in_use (409, holders in details) while other
// clients hold the audience.
export function useChangeClientType(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (appType: "api" | "worker") => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/type`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ appType }),
        },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", clientId] });
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authorizedFetch(`${API_BASE}/clients/${id}`, {
        method: "DELETE",
      });
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      // Platforms embed their client list, so the owning platform must refetch too.
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
    },
  });
}

export function useGetSecret(clientId: string, enabled = true) {
  return useQuery({
    queryKey: ["clients", clientId, "secret"],
    enabled: !!clientId && enabled,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/secret`,
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<ClientSecretResponse>;
    },
  });
}

export function useRevealSecret(clientId: string) {
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/secret?reveal=true`,
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<ClientSecretResponse>;
    },
  });
}

export function useRotateSecret(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/secret`,
        { method: "POST" },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<ClientSecretResponse>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["clients", clientId, "secret"], {
        ...data,
        secret: null,
      });
    },
  });
}

// The `<clientId>-local` companion (ADR-0046 decision 3). Always 200, never
// 404 for "not enabled" — the Portal shows one client with a Local
// development section, never a second client.
export function useLocalClient(clientId: string) {
  return useQuery({
    queryKey: ["clients", clientId, "local-client"],
    enabled: !!clientId,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/local-client`,
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<LocalClientResponse>;
    },
  });
}

export function useEnableLocalClient(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/local-client`,
        { method: "POST" },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<LocalClientResponse>;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["clients", clientId, "local-client"], data);
      // A disable/enable cycle can reuse the same companion id — drop any
      // secret revealed under the old lifetime so it isn't shown as current.
      if (data.id) {
        queryClient.removeQueries({ queryKey: ["clients", data.id, "secret"] });
      }
    },
  });
}

export function useDisableLocalClient(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/clients/${clientId}/local-client`,
        { method: "DELETE" },
      );
      await throwIfNotOk(res, "kc");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "local-client"],
      });
    },
  });
}

export function useSearchUsers(query: string) {
  return useQuery({
    queryKey: ["users", "search", query],
    enabled: query.length >= 2,
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/users/search?q=${encodeURIComponent(query)}`,
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<UserSearchResult[]>;
    },
    staleTime: 1000 * 30,
  });
}

/**
 * Resolve a single user's display name by Keycloak GUID — the fallback for tables that show a
 * raw subject id because the source endpoint didn't (or couldn't) enrich it server-side.
 */
export function useUser(userId: string | null) {
  return useQuery({
    queryKey: ["users", userId],
    enabled: !!userId,
    staleTime: 1000 * 60,
    queryFn: async () => {
      const res = await authorizedFetch(`${API_BASE}/users/${userId}`);
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<{
        id: string;
        username: string | null;
        displayName: string;
        email: string | null;
      }>;
    },
  });
}

export interface GrantProviderReadersResult {
  total: number;
  granted: number;
  // A member who already held the role — distinct from skipped, which means the
  // member had no Keycloak user id at all (#480).
  unchanged: number;
  skipped: number;
  // Members whose grant a rule refused. Each is named in errors; the rest of the
  // run went through, because a bulk operation is many independent intents (ADR-0047).
  failed: number;
  errors: string[];
}

export function useGrantProviderReaders() {
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/users/grant-provider-readers`,
        {
          method: "POST",
        },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<GrantProviderReadersResult>;
    },
  });
}

export function useGrantKeycloakViewers() {
  return useMutation({
    mutationFn: async () => {
      const res = await authorizedFetch(
        `${API_BASE}/users/grant-keycloak-viewers`,
        {
          method: "POST",
        },
      );
      await throwIfNotOk(res, "kc");
      return res.json() as Promise<GrantProviderReadersResult>;
    },
  });
}
